"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useOrganization } from "@/hooks/useOrganization";
import { useProjectsHomeHref } from "@/hooks/useProjectsHomeHref";
import { useThreadPermissions } from "@/hooks/useThreadPermissions";
import { useOrgTeamPath } from "@/lib/orgTeamPath";
import type { SearchResultItem } from "./GlobalSearch.types";
export function useNavigationSearchItems() {
  const $ = _c(71);
  const t = useTranslations("navigation");
  const tSearch = useTranslations("global_search");
  const tSettings = useTranslations("settings");
  const flags = useFeatureFlags();
  const {
    currentOrganization
  } = useOrganization();
  const {
    build
  } = useOrgTeamPath();
  const {
    href: projectsHomeLogicalPath
  } = useProjectsHomeHref();
  const {
    canUseProjectAssist
  } = useThreadPermissions(undefined);
  const pathname = usePathname();
  const showContentMigration = !!flags["users-migration-wizard"] && !!currentOrganization?.attributes?.salesforceId;
  let items;
  if ($[0] !== build || $[1] !== canUseProjectAssist || $[2] !== flags || $[3] !== pathname || $[4] !== projectsHomeLogicalPath || $[5] !== showContentMigration || $[6] !== t || $[7] !== tSearch || $[8] !== tSettings) {
    const nav = tSearch("categories.navigation");
    const settings = tSearch("categories.settings");
    items = [{
      id: "nav-files",
      category: "navigation",
      title: t("items.files"),
      subtitle: nav,
      href: build(projectsHomeLogicalPath),
      keywords: ["projects", "documents", "files"]
    }, {
      id: "settings-general",
      category: "navigation",
      title: t("actions.settings"),
      subtitle: settings,
      href: build("/settings/general"),
      keywords: ["organization", "general", "settings"]
    }, {
      id: "settings-sso",
      category: "navigation",
      title: tSettings("general_enable_sso"),
      subtitle: settings,
      href: build("/settings/general"),
      keywords: ["SSO", "single sign-on", "authentication", "SAML"],
      highlightId: "configurations"
    }, {
      id: "settings-sso-domains",
      category: "navigation",
      title: tSettings("general_sso_domains"),
      subtitle: settings,
      href: build("/settings/general"),
      keywords: ["SSO", "domains", "email", "redirect"],
      highlightId: "sso-domains"
    }, {
      id: "settings-data-protection",
      category: "navigation",
      title: tSettings("general_enable_data_protection"),
      subtitle: settings,
      href: build("/settings/general"),
      keywords: ["data protection", "security", "privacy"],
      highlightId: "configurations"
    }, {
      id: "settings-procore",
      category: "navigation",
      title: tSettings("general_enable_procore"),
      subtitle: settings,
      href: build("/settings/general"),
      keywords: ["procore", "integration"],
      highlightId: "integrations"
    }, {
      id: "settings-onedrive",
      category: "navigation",
      title: tSettings("general_enable_onedrive"),
      subtitle: settings,
      href: build("/settings/general"),
      keywords: ["onedrive", "microsoft", "integration"],
      highlightId: "integrations"
    }, {
      id: "settings-salesforce",
      category: "navigation",
      title: tSearch("items.salesforce_id"),
      subtitle: settings,
      href: build("/settings/general"),
      keywords: ["salesforce", "CRM", "integration"],
      highlightId: "salesforce-id"
    }, {
      id: "settings-users",
      category: "navigation",
      title: tSearch("items.user_management"),
      subtitle: settings,
      href: build("/settings/users"),
      keywords: ["users", "members", "invite", "organization members"]
    }, {
      id: "settings-teams",
      category: "navigation",
      title: tSearch("items.team_management"),
      subtitle: settings,
      href: build("/settings/teams"),
      keywords: ["teams", "create team", "team settings"]
    }, {
      id: "settings-templates",
      category: "navigation",
      title: tSettings("nav_templates"),
      subtitle: settings,
      href: build("/settings/builder/templates"),
      keywords: ["templates", "checklist", "builder"]
    }, {
      id: "settings-template-grants",
      category: "navigation",
      title: tSettings("nav_template_grants"),
      subtitle: settings,
      href: build("/settings/builder/template-grants"),
      keywords: ["template grants", "access", "permissions", "checklist"]
    }];
    if (canUseProjectAssist) {
      items.splice(1, 0, {
        id: "nav-chat",
        category: "navigation",
        title: t("items.chat"),
        subtitle: nav,
        href: build("/chat"),
        keywords: ["ai", "assistant", "ask", "query"]
      });
    }
    if (showContentMigration) {
      let t0;
      if ($[10] !== tSearch) {
        t0 = tSearch("items.content_migration");
        $[10] = tSearch;
        $[11] = t0;
      } else {
        t0 = $[11];
      }
      let t1;
      if ($[12] !== build) {
        t1 = build("/settings/content-migration");
        $[12] = build;
        $[13] = t1;
      } else {
        t1 = $[13];
      }
      let t2;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = ["migration", "import", "transfer", "content"];
        $[14] = t2;
      } else {
        t2 = $[14];
      }
      let t3;
      if ($[15] !== settings || $[16] !== t0 || $[17] !== t1) {
        t3 = {
          id: "settings-content-migration",
          category: "navigation",
          title: t0,
          subtitle: settings,
          href: t1,
          keywords: t2
        };
        $[15] = settings;
        $[16] = t0;
        $[17] = t1;
        $[18] = t3;
      } else {
        t3 = $[18];
      }
      items.push(t3);
    }
    if (flags["settings-show-language-selector-ff-78"]) {
      let t0;
      if ($[19] !== tSettings) {
        t0 = tSettings("language");
        $[19] = tSettings;
        $[20] = t0;
      } else {
        t0 = $[20];
      }
      let t1;
      if ($[21] !== build) {
        t1 = build("/profile");
        $[21] = build;
        $[22] = t1;
      } else {
        t1 = $[22];
      }
      let t2;
      if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = ["language", "locale", "espa\xF1ol", "english", "idioma"];
        $[23] = t2;
      } else {
        t2 = $[23];
      }
      let t3;
      if ($[24] !== settings || $[25] !== t0 || $[26] !== t1) {
        t3 = {
          id: "settings-language",
          category: "navigation",
          title: t0,
          subtitle: settings,
          href: t1,
          keywords: t2,
          highlightId: "language"
        };
        $[24] = settings;
        $[25] = t0;
        $[26] = t1;
        $[27] = t3;
      } else {
        t3 = $[27];
      }
      items.push(t3);
    }
    if (flags["app-show-onboarding-tasks-ff-143"]) {
      let t0;
      if ($[28] !== t) {
        t0 = t("items.getting_started_guide");
        $[28] = t;
        $[29] = t0;
      } else {
        t0 = $[29];
      }
      const t1 = `${pathname}?onboarding=1`;
      let t2;
      if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = ["onboarding", "getting started", "tutorial", "guide", "walkthrough", "intro", "welcome", "tips", "help"];
        $[30] = t2;
      } else {
        t2 = $[30];
      }
      let t3;
      if ($[31] !== nav || $[32] !== t0 || $[33] !== t1) {
        t3 = {
          id: "nav-getting-started-guide",
          category: "navigation",
          title: t0,
          subtitle: nav,
          href: t1,
          keywords: t2
        };
        $[31] = nav;
        $[32] = t0;
        $[33] = t1;
        $[34] = t3;
      } else {
        t3 = $[34];
      }
      items.push(t3);
    }
    if (flags["usage-dashboard"]) {
      let t0;
      if ($[35] !== t) {
        t0 = t("items.usage_dashboard");
        $[35] = t;
        $[36] = t0;
      } else {
        t0 = $[36];
      }
      let t1;
      if ($[37] !== build) {
        t1 = build("/usage-dashboard");
        $[37] = build;
        $[38] = t1;
      } else {
        t1 = $[38];
      }
      let t2;
      if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = ["usage", "analytics", "dashboard", "statistics"];
        $[39] = t2;
      } else {
        t2 = $[39];
      }
      let t3;
      if ($[40] !== nav || $[41] !== t0 || $[42] !== t1) {
        t3 = {
          id: "nav-usage-dashboard",
          category: "navigation",
          title: t0,
          subtitle: nav,
          href: t1,
          keywords: t2
        };
        $[40] = nav;
        $[41] = t0;
        $[42] = t1;
        $[43] = t3;
      } else {
        t3 = $[43];
      }
      items.push(t3);
    }
    if (flags["sidebar-show-assigned-to-me-ff-73"]) {
      let t0;
      if ($[44] !== t) {
        t0 = t("items.assigned_to_me");
        $[44] = t;
        $[45] = t0;
      } else {
        t0 = $[45];
      }
      let t1;
      if ($[46] !== build) {
        t1 = build("/assigned-to-me");
        $[46] = build;
        $[47] = t1;
      } else {
        t1 = $[47];
      }
      let t2;
      if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = ["tasks", "assignments", "checklist"];
        $[48] = t2;
      } else {
        t2 = $[48];
      }
      let t3;
      if ($[49] !== nav || $[50] !== t0 || $[51] !== t1) {
        t3 = {
          id: "nav-assigned-to-me",
          category: "navigation",
          title: t0,
          subtitle: nav,
          href: t1,
          keywords: t2
        };
        $[49] = nav;
        $[50] = t0;
        $[51] = t1;
        $[52] = t3;
      } else {
        t3 = $[52];
      }
      items.push(t3);
    }
    if (flags["sidebar-show-shared-with-me-ff-73"]) {
      let t0;
      if ($[53] !== t) {
        t0 = t("items.shared_with_me");
        $[53] = t;
        $[54] = t0;
      } else {
        t0 = $[54];
      }
      let t1;
      if ($[55] !== build) {
        t1 = build("/shared-with-me");
        $[55] = build;
        $[56] = t1;
      } else {
        t1 = $[56];
      }
      let t2;
      if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = ["shared", "collaboration"];
        $[57] = t2;
      } else {
        t2 = $[57];
      }
      let t3;
      if ($[58] !== nav || $[59] !== t0 || $[60] !== t1) {
        t3 = {
          id: "nav-shared-with-me",
          category: "navigation",
          title: t0,
          subtitle: nav,
          href: t1,
          keywords: t2
        };
        $[58] = nav;
        $[59] = t0;
        $[60] = t1;
        $[61] = t3;
      } else {
        t3 = $[61];
      }
      items.push(t3);
    }
    let t0;
    if ($[62] !== t) {
      t0 = t("items.archive");
      $[62] = t;
      $[63] = t0;
    } else {
      t0 = $[63];
    }
    let t1;
    if ($[64] !== build) {
      t1 = build("/archive");
      $[64] = build;
      $[65] = t1;
    } else {
      t1 = $[65];
    }
    let t2;
    if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = ["archived", "closed", "lost", "old", "inactive"];
      $[66] = t2;
    } else {
      t2 = $[66];
    }
    let t3;
    if ($[67] !== nav || $[68] !== t0 || $[69] !== t1) {
      t3 = {
        id: "nav-archive",
        category: "navigation",
        title: t0,
        subtitle: nav,
        href: t1,
        keywords: t2
      };
      $[67] = nav;
      $[68] = t0;
      $[69] = t1;
      $[70] = t3;
    } else {
      t3 = $[70];
    }
    items.push(t3);
    $[0] = build;
    $[1] = canUseProjectAssist;
    $[2] = flags;
    $[3] = pathname;
    $[4] = projectsHomeLogicalPath;
    $[5] = showContentMigration;
    $[6] = t;
    $[7] = tSearch;
    $[8] = tSettings;
    $[9] = items;
  } else {
    items = $[9];
  }
  return items;
}